/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    ChannelSettings,
    ChannelSettingsApi,
    ChannelSettingsStoreRequest,
    Configuration,
    ConfigurationParameters,
} from 'client-code/channel-settings';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { Channel } from '../entities';
import { IChannelSettingsServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import { TChannelV2, TServicesParams } from '../types';

export class ChannelSettingsServiceRepositoryImpl implements IChannelSettingsServiceRepository {
    private mParams: TServicesParams;

    private TAG: string = 'ChannelSettingsServiceRepositoryImpl';

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    /**
     * The Api request the following parameters:
     * - channel number as string
     * - isFavorite flag as boolean
     */
    public async setChannelSettings(
        channel: Channel | TChannelV2,
        isFavorite: boolean,
    ): Promise<boolean> {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE).headers;
            const config: ConfigurationParameters = {
                basePath: this.mParams.basePath,
                apiKey: this.mParams.accessToken,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const channelSettings: ChannelSettings = {
                channelNumber: `${channel.channelNumber}`,
                isFavorite,
            };

            const requestParameters: ChannelSettingsStoreRequest = {
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationName: commonHeaders.ApplicationName,
                applicationVersion: commonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                contentType: '',
                deviceType: commonHeaders.DeviceType,
                productName: commonHeaders.ProductName,
                channelSettings: [channelSettings],
                whiny: this.mParams.whiny || 'false',
            };

            const requestConfiguration = new Configuration(config);
            const channelSettingsApi = new ChannelSettingsApi(requestConfiguration);
            const channelSettingsResponse =
                await channelSettingsApi.channelSettingsStore(requestParameters);

            if (channelSettingsResponse.status === 204) return true;
            throw new Error();
        } catch (error: any) {
            Log.e(this.TAG, 'setChannelSettings()', error.message, {
                channel,
            });

            return false;
        }
    }
}
